import { handleResponse } from './handleResponse';
import apiAxios from './apiAxios';
import {
  CreateConfirmedReservationRequest,
  CreateNewReservationParams,
  CreateNewSlotParams,
  EditReservationParams,
  EventSchedule,
  FinishSlotReservationParams,
  GetActionableReservationsResponse,
  GetDaySlotsResponse,
  GetTimeslotsParams,
  GetTimeslotsResponse,
  ReservationsResponse,
  ReserveBusinessEventParams,
  SlotReservationDetailsResponse,
  SlotReservationResponse,
  SlotReservationsCheckinsResponse,
  TimeReservationDetailsResponse,
} from '@data/types/reservations';

const BASE_COMPANY_ROUTE = '/api/companies';
const BASE_COMPANY_ROUTE_V2 = '/api/v2/companies';
const BASE_COMPANY_ROUTE_V3 = '/api/v3/companies';
const BASE_RESERVATION_ROUTE = '/api/reservations';
const BASE_RESERVATION_ROUTE_V2 = '/api/v2/reservations';
const BASE_TIMESLOTS_V3 = '/api/v3/timeslots';

const reservationsApi = {
  async getReservations(companyId: string): Promise<ReservationsResponse> {
    return apiAxios
      .get(`${BASE_COMPANY_ROUTE_V2}/${companyId}/reservations`)
      .then(handleResponse);
  },
  async getReservationsSchedule(
    companyId: string,
    params: {
      startDate?: string;
      endDate?: string;
      calendarId?: string;
      date?: string;
    }
  ): Promise<EventSchedule[]> {
    return apiAxios
      .get(`${BASE_COMPANY_ROUTE_V3}/${companyId}/schedule`, { params })
      .then(handleResponse);
  },
  async getTimeReservationDetails(
    companyId: string,
    reservationId: string
  ): Promise<TimeReservationDetailsResponse> {
    return apiAxios
      .get(`${BASE_COMPANY_ROUTE}/${companyId}/reservations/${reservationId}`)
      .then(handleResponse);
  },
  async getSlotReservationDetails(
    companyId: string,
    slotId: string
  ): Promise<SlotReservationDetailsResponse> {
    return apiAxios
      .get(`${BASE_COMPANY_ROUTE}/${companyId}/slots/${slotId}`)
      .then(handleResponse);
  },
  async getSlotReservationsList(
    companyId: string,
    slotId: string
  ): Promise<SlotReservationResponse[]> {
    return apiAxios
      .get(`${BASE_COMPANY_ROUTE}/${companyId}/slots/${slotId}/reservations`)
      .then(handleResponse);
  },
  async getSlotReservationsCheckins(
    companyId: string,
    slotId: string
  ): Promise<SlotReservationsCheckinsResponse[]> {
    return apiAxios
      .get(
        `${BASE_COMPANY_ROUTE}/${companyId}/slots/${slotId}/reservations/checkins`
      )
      .then(handleResponse);
  },
  async setSlotReservationCheckin(
    companyId: string,
    slotId: string,
    data: { checkedIn: boolean; reservationId: string }
  ): Promise<void> {
    return apiAxios
      .post(
        `${BASE_COMPANY_ROUTE}/${companyId}/slots/${slotId}/reservations/checkins`,
        data
      )
      .then(handleResponse);
  },
  async cancelTimeReservation(
    reservationId: string,
    data: { comment: string; deleteFuture: boolean }
  ): Promise<{ status: string }> {
    return apiAxios
      .post(`${BASE_RESERVATION_ROUTE}/${reservationId}/cancel`, data)
      .then(handleResponse);
  },
  async cancelSlotReservation(
    companyId: string,
    slotId: string,
    data?: {
      comment?: string;
      cancelFutureSlots?: boolean;
    }
  ): Promise<void> {
    return apiAxios
      .post(`${BASE_COMPANY_ROUTE}/${companyId}/slots/${slotId}/cancel`, data)
      .then(handleResponse);
  },
  async confirmTimeReservation(
    reservationId: string,
    comment?: string
  ): Promise<{ status: string }> {
    return apiAxios
      .post(`${BASE_RESERVATION_ROUTE}/${reservationId}/confirm`, {
        comment,
      })
      .then(handleResponse);
  },
  async finishTimeReservation(
    reservationId: string,
    data: { comment?: string; discount?: number; noShow: boolean }
  ): Promise<void> {
    return apiAxios
      .post(`${BASE_RESERVATION_ROUTE}/${reservationId}/finish`, data)
      .then(handleResponse);
  },
  async finishSlotReservation(
    companyId: string,
    slotId: string,
    data: { reservations: FinishSlotReservationParams[] }
  ): Promise<void> {
    return apiAxios
      .post(`${BASE_COMPANY_ROUTE}/${companyId}/slots/${slotId}/finish`, data)
      .then(handleResponse);
  },
  async updateSlotReservationSpots(
    companyId: string,
    slotId: string,
    reservationId: string,
    spots: number,
    tickets?: {
      slotTicketId: string;
      spots: number;
    }[]
  ): Promise<void> {
    return apiAxios.put(
      `${BASE_COMPANY_ROUTE}/${companyId}/slots/${slotId}/reservations/${reservationId}`,
      { spots, tickets }
    );
  },
  async getAllDailySlots(
    companyId: string,
    date: string
  ): Promise<GetDaySlotsResponse[]> {
    return apiAxios
      .get(`${BASE_COMPANY_ROUTE}/${companyId}/slots`, {
        params: { date },
      })
      .then(handleResponse);
  },
  async createNewReservation(
    data: CreateNewReservationParams
  ): Promise<{ id: string }> {
    return apiAxios
      .post(`${BASE_RESERVATION_ROUTE_V2}/create`, data)
      .then(handleResponse);
  },
  async createNewSlot(
    companyId: string,
    data: CreateNewSlotParams
  ): Promise<{ id: string }> {
    return apiAxios
      .post(`${BASE_COMPANY_ROUTE}/${companyId}/slots`, data)
      .then(handleResponse);
  },
  async getTimeslots(data: GetTimeslotsParams): Promise<GetTimeslotsResponse> {
    return apiAxios.post(BASE_TIMESLOTS_V3, data).then(handleResponse);
  },
  async reserveBusinessEvent(
    data: ReserveBusinessEventParams
  ): Promise<{ id: string }> {
    return apiAxios
      .post(`${BASE_RESERVATION_ROUTE_V2}/reserve`, data)
      .then(handleResponse);
  },
  async getActionableReservations(
    companyId: string
  ): Promise<GetActionableReservationsResponse> {
    return apiAxios
      .get(`${BASE_COMPANY_ROUTE_V2}/${companyId}/reservations/actionable`)
      .then(handleResponse);
  },
  async editReservation(
    reservationId: string,
    data: EditReservationParams
  ): Promise<{ id: string }> {
    return apiAxios
      .put(`${BASE_RESERVATION_ROUTE}/${reservationId}`, data)
      .then(handleResponse);
  },
  async editSlot(
    companyId: string,
    slotId: string,
    data: {
      calendarId: string;
      comment?: string;
      duration: number;
      spotCount: number;
      spotPrice: number;
      startDate: string;
      tickets?: {
        price: number;
        slotTicketId: string;
      }[];
      updateFutureSlots?: boolean;
    }
  ): Promise<{ id: string }> {
    return apiAxios
      .put(`${BASE_COMPANY_ROUTE}/${companyId}/slots/${slotId}`, data)
      .then(handleResponse);
  },
  async moveReservation(
    reservationId: string,
    data: {
      calendarId: string;
      startDate: string;
      slotId?: string;
    }
  ): Promise<void> {
    return apiAxios
      .put(`${BASE_RESERVATION_ROUTE}/${reservationId}/move`, data)
      .then(handleResponse);
  },
  async moveSlot(
    companyId: string,
    slotId: string,
    data: {
      calendarId: string;
      startDate: string;
      updateFutureSlots: boolean;
    }
  ): Promise<void> {
    return apiAxios
      .post(`${BASE_COMPANY_ROUTE}/${companyId}/slots/${slotId}/move`, data)
      .then(handleResponse);
  },
  async createConfirmedReservation(
    companyId: string,
    data: CreateConfirmedReservationRequest
  ): Promise<{ id: string; slotId?: string }> {
    return apiAxios
      .post(`api/reservations/business/create`, data)
      .then(handleResponse);
  },
  async editSlotReservation(
    companyId: string,
    slotId: string,
    reservationId: string,
    data: {
      comment?: string;
      spots?: number;
      tickets?: {
        slotTicketId: string;
        spots: number;
      }[];
    }
  ): Promise<void> {
    console.log(data);
    return apiAxios
      .put(
        `${BASE_COMPANY_ROUTE}/${companyId}/slots/${slotId}/reservations/${reservationId}`,
        data
      )
      .then(handleResponse);
  },
};

export default reservationsApi;
